export function sortByField(data, fieldName) {
    return [...(data || [])].sort((a, b) => String(a?.[fieldName] ?? '').localeCompare(String(b?.[fieldName] ?? ''), undefined, { sensitivity: 'base' }))
}

export function getCurrentData(){
    var today = new Date();
    var dd = String(today.getDate()).padStart(2, '0');
    var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    var yyyy = today.getFullYear();
    today = dd + '-' + mm + '-' + yyyy;
    return today
}

export function getCurrentTimestamp() {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  const hh = String(today.getHours()).padStart(2, '0');
  const min = String(today.getMinutes()).padStart(2, '0');
  const ss = String(today.getSeconds()).padStart(2, '0');
  return `${hh}:${min}:${ss} ${dd}-${mm}-${yyyy}`;
}

/**
 * Parses the date formats used by the backend ("yyyy-MM-dd", "dd-MM-yyyy", ISO strings).
 * `new Date("dd-MM-yyyy")` is Invalid Date on Safari/iOS, so never rely on the native parser.
 */
export function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const str = String(value).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

export function isDateOlderThanAnDateToRemove(dateString, dateToRemove) {
  const inputDate = parseDate(dateString);
  if (!inputDate) return true;
  const limit = new Date();
  limit.setDate(limit.getDate() - dateToRemove);
  return inputDate < limit;
}

/** "3 days ago" / "3 giorni fa" in the current language, falling back to a short date. */
export function formatRelativeDate(value, lang = 'en') {
  const date = parseDate(value);
  if (!date) return value || '';
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date - startOfToday) / 86400000);
  try {
    if (Math.abs(diffDays) < 30) {
      return new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }).format(diffDays, 'day');
    }
    return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

export function formatDate(value, lang = 'en') {
  const date = parseDate(value);
  if (!date) return value || '';
  try {
    return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

/** Localised country name from an ISO code (e.g. "IT" -> "Italia"). */
export function regionName(code, lang = 'en', fallback = '') {
  if (!code || String(code).length !== 2) return fallback || code || '';
  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(String(code).toUpperCase()) || fallback || code;
  } catch {
    return fallback || code;
  }
}

export function formatSalary(amount, currency, lang = 'en') {
  if (amount === null || amount === undefined || amount === '' || Number(amount) === 0) return '';
  const n = Number(amount);
  if (isNaN(n)) return `${amount} ${currency || ''}`.trim();
  const iso = String(currency || '').match(/[A-Z]{3}/)?.[0];
  try {
    if (iso) return new Intl.NumberFormat(lang, { style: 'currency', currency: iso, maximumFractionDigits: 0 }).format(n);
  } catch { /* unknown currency code */ }
  return `${new Intl.NumberFormat(lang).format(n)} ${currency || ''}`.trim();
}

export const sliderMarksSalary = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => ({ value, label: `${value}K` }));

export function truncateText(text, maxLength) {
  if (!text) return '';
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
}

export function stripHtml(html) {
  if (!html) return '';
  try {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
  } catch {
    return String(html).replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
  }
}

/**
 * Returns the inappropriate words found in `text`.
 * languageTag: ISO code of the post nation (null = check every language, used for remote posts).
 */
export function findBadWords(text, badWords, languageTag) {
  if (!text || !Array.isArray(badWords) || badWords.length === 0) return [];
  const tag = languageTag ? String(languageTag).toLowerCase() : null;
  const active = new Set(
    badWords
      .filter((item) => item && item.isActive && item.word)
      .filter((item) => !tag || item.language?.toLowerCase() === tag)
      .map((item) => item.word.toLowerCase())
  );
  if (active.size === 0) return [];
  const words = String(text)
    .replace(/<\/?[^>]+(>|$)/g, ' ')
    .toLowerCase()
    .split(/[\s.,;:!?()"'«»]+/)
    .filter(Boolean);
  return [...new Set(words.filter((w) => active.has(w)))];
}

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim());

export const isValidUrl = (url) => {
  const value = String(url || '').trim();
  if (!value) return false;
  try {
    const parsed = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    return Boolean(parsed.hostname && parsed.hostname.includes('.'));
  } catch {
    return false;
  }
};

export const normalizeUrl = (url) => {
  const value = String(url || '').trim();
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};
